import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { BlindCloseForm } from '../components/BlindCloseForm';
import { SessionConferenceModal } from '../components/SessionConferenceModal';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Lock, Unlock, UserCircle, Computer, X, ShieldCheck, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { overlayClose } from '../lib/overlayClose';
import { CLOSING_METHODS, emptyCounted, countedToBreakdown } from '../lib/closingMethods';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_LABELS = {
  dinheiro: 'Dinheiro',
  pix_maquininha: 'PIX Maquininha',
  debito: 'Cartão de Débito',
  credito: 'Cartão de Crédito',
  voucher: 'Voucher',
  pix: 'PIX (conta da loja)',
  cheque: 'Cheque',
  cartao: 'Cartão (legado)',
  crediario: 'Crediário',
  a_prazo: 'Venda a Prazo',
};

const BreakdownSection = ({ sessionId, countedAmount = 0 }) => {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    const fetchIt = async () => {
      try {
        const r = await axios.get(`${API}/cash-sessions/${sessionId}/breakdown`, { withCredentials: true });
        if (alive) setData(r.data);
      } catch (_e) { /* breakdown opcional — ignora erro */ }
    };
    fetchIt();
    const id = setInterval(fetchIt, 15000); // refresh a cada 15s
    return () => { alive = false; clearInterval(id); };
  }, [sessionId]);

  if (!data) return null;
  const b = data.breakdown || {};
  const expected = data.expected_amount || 0;
  const diff = countedAmount > 0 ? +(countedAmount - expected).toFixed(2) : null;

  return (
    <div className="bg-slate-50 rounded-md p-3 border border-slate-200" data-testid="cash-breakdown">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">Vendas por forma de pagamento</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
        {Object.entries(PAYMENT_LABELS).map(([k, label]) => {
          const val = Number(b[k] || 0);
          if (val === 0 && k !== 'dinheiro') return null;
          return (
            <div key={k} className="flex justify-between bg-white rounded px-2 py-1 border border-slate-200" data-testid={`breakdown-${k}`}>
              <span className="text-slate-600">{label}</span>
              <span className="font-bold text-slate-900">R$ {val.toFixed(2)}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-2 pt-2 border-t border-slate-300 flex justify-between text-sm font-bold">
        <span className="text-slate-700">TOTAL DE VENDAS</span>
        <span className="text-blue-700">R$ {Number(b.total || 0).toFixed(2)}</span>
      </div>
      {diff !== null && (
        <div className={`mt-2 p-2 rounded font-bold text-sm ${
          diff === 0 ? 'bg-emerald-100 text-emerald-800' :
          diff > 0 ? 'bg-blue-100 text-blue-800' :
          'bg-red-100 text-red-800'
        }`} data-testid="close-diff">
          {diff === 0
            ? '✓ Caixa fecha exato (sem diferença)'
            : diff > 0
              ? `↑ SOBRA: R$ ${diff.toFixed(2)} (contado R$ ${countedAmount.toFixed(2)} − esperado R$ ${expected.toFixed(2)})`
              : `↓ FALTA: R$ ${Math.abs(diff).toFixed(2)} (contado R$ ${countedAmount.toFixed(2)} − esperado R$ ${expected.toFixed(2)})`
          }
        </div>
      )}
    </div>
  );
};

export const CashSessionPage = () => (
  <Layout>
    <CashSessionContent />
  </Layout>
);

export const CashSessionContent = () => {
  const { user: currentUser } = useAuth();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [operators, setOperators] = useState([]);
  const [openSessions, setOpenSessions] = useState([]); // todos caixas abertos (admin view)
  const [pendingSessions, setPendingSessions] = useState([]); // caixas em pending_close aguardando contagem
  const [confModalSessionId, setConfModalSessionId] = useState(null); // sessão selecionada p/ conferência
  const [openForm, setOpenForm] = useState({ initial_amount: '', terminal: '', notes: '', operator_id: '', operator_password: '' });
  const [showCloseForm, setShowCloseForm] = useState(false);
  // Quando a sessão está em pending_close, deixamos o operador/admin escolher:
  // - Botão "Conferir Turno (Modal Completo)" abre SessionConferenceModal com a_prazo/sangrias/etc.
  // Não abrimos mais o form inline automaticamente para evitar confusão com o novo fluxo.
  useEffect(() => {
    setShowCloseForm(false);
  }, [current?.status]);
  const [openForOther, setOpenForOther] = useState(false);  // admin: abrir pra outro operador?
  const [closeForm, setCloseForm] = useState({ counted_amount: '', counted: emptyCounted(), notes: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Modal admin: fechar caixa de outro operador
  const [adminClose, setAdminClose] = useState(null); // { session, counted, notes }
  const [transferModal, setTransferModal] = useState(null); // { session, new_operator_id, new_operator_password }
  // Modal de sangria (retirada de dinheiro do caixa, mantém o caixa aberto)
  const [sangriaModal, setSangriaModal] = useState(null); // { amount, reason }
  // Modal de suprimento (entrada de dinheiro no caixa, mantém o caixa aberto)
  const [suprimentoModal, setSuprimentoModal] = useState(null); // { amount, reason }

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const [c, h, ops, openAll, pending] = await Promise.all([
        axios.get(`${API}/cash-sessions/current`, { withCredentials: true }),
        axios.get(`${API}/cash-sessions/history?only_mine=true&limit=10`, { withCredentials: true }),
        axios.get(`${API}/users/operators`, { withCredentials: true }),
        axios.get(`${API}/cash-sessions/open-now`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/cash-sessions/pending-close`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setCurrent(c.data);
      setHistory(h.data);
      setOperators(ops.data);
      setOpenSessions(openAll.data || []);
      setPendingSessions(pending.data || []);
    } catch (err) { console.error(err); }
  };

  const handleOpen = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccess('');
    try {
      const payload = {
        initial_amount: parseFloat(openForm.initial_amount) || 0,
        terminal: openForm.terminal || null,
        notes: openForm.notes || null,
      };
      // Admin marcando "abrir para outro operador" envia operator_id + senha do outro.
      // Caso contrário, abre o caixa do usuário logado (sem precisar de senha).
      if (openForForOther(isAdmin) && openForm.operator_id) {
        payload.operator_id = openForm.operator_id;
        payload.operator_password = openForm.operator_password;
      }
      await axios.post(`${API}/cash-sessions/open`, payload, { withCredentials: true });
      setOpenForm({ initial_amount: '', terminal: '', notes: '', operator_id: '', operator_password: '' });
      setOpenForOther(false);
      setSuccess('Caixa aberto! Bom trabalho 🚀');
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao abrir caixa');
    } finally { setLoading(false); }
  };

  // helper para evitar TDZ: lê o flag openForOther
  function openForForOther(adminFlag) { return adminFlag && openForOther; }

  const handleClose = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccess('');
    try {
      await axios.post(`${API}/cash-sessions/close`, {
        counted_amount: parseFloat(closeForm.counted_amount) || 0,
        notes: closeForm.notes || null,
        counted_breakdown: { ...countedToBreakdown(closeForm.counted), dinheiro: parseFloat(closeForm.counted_amount) || 0 },
      }, { withCredentials: true });
      setCloseForm({ counted_amount: '', counted: emptyCounted(), notes: '' });
      setSuccess('Caixa fechado com sucesso.');
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao fechar caixa');
    } finally { setLoading(false); }
  };

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager';

  const handleSangria = async (e) => {
    e.preventDefault();
    if (!sangriaModal) return;
    const amount = parseFloat(sangriaModal.amount);
    if (!amount || amount <= 0) {
      setError('Informe um valor de sangria maior que zero.');
      return;
    }
    if (!sangriaModal.reason || sangriaModal.reason.trim().length < 3) {
      setError('Informe o motivo da sangria (mín. 3 caracteres).');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/cashflow`, {
        type: 'saida',
        category: 'sangria',
        amount,
        description: `Sangria — ${sangriaModal.reason.trim()}`,
        payment_method: 'dinheiro',
        notes: null,
      }, { withCredentials: true });
      setSuccess(`Sangria de R$ ${amount.toFixed(2)} registrada. Caixa segue aberto.`);
      setSangriaModal(null);
      setTimeout(() => setSuccess(''), 5000);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao registrar sangria');
    } finally {
      setLoading(false);
    }
  };

  const handleSuprimento = async (e) => {
    e.preventDefault();
    if (!suprimentoModal) return;
    const amount = parseFloat(suprimentoModal.amount);
    if (!amount || amount <= 0) {
      setError('Informe um valor de suprimento maior que zero.');
      return;
    }
    if (!suprimentoModal.reason || suprimentoModal.reason.trim().length < 3) {
      setError('Informe o motivo do suprimento (mín. 3 caracteres).');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/cashflow`, {
        type: 'entrada',
        category: 'suprimento',
        amount,
        description: `Suprimento — ${suprimentoModal.reason.trim()}`,
        payment_method: 'dinheiro',
        notes: null,
      }, { withCredentials: true });
      setSuccess(`Suprimento de R$ ${amount.toFixed(2)} registrado. Caixa segue aberto.`);
      setSuprimentoModal(null);
      setTimeout(() => setSuccess(''), 5000);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao registrar suprimento');
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!transferModal) return;
    if (!transferModal.new_operator_id) {
      setError('Selecione o novo operador');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = { new_operator_id: transferModal.new_operator_id };
      if (!isAdmin) {
        payload.new_operator_password = transferModal.new_operator_password;
      }
      const r = await axios.post(
        `${API}/cash-sessions/${transferModal.session.id}/transfer`,
        payload,
        { withCredentials: true }
      );
      setSuccess(`Caixa transferido para ${r.data.user_name} com sucesso!`);
      setTransferModal(null);
      setTimeout(() => setSuccess(''), 5000);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao transferir caixa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="p-8 max-w-6xl" data-testid="cash-session-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-md bg-emerald-600 flex items-center justify-center">
            <UserCircle className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-4xl font-black text-slate-900 tracking-tight">Abrir / Fechar Caixa</h1>
            <p className="text-sm text-slate-600">Cada usuário tem o seu próprio caixa. Fechamento individual.</p>
          </div>
        </div>

        {/* Banner verde de abertura/fechamento */}
        <div className={`rounded-md text-white p-4 mb-6 flex items-center justify-between flex-wrap gap-3 ${current?.status === 'pending_close' ? 'bg-gradient-to-r from-orange-500 to-orange-600' : 'bg-gradient-to-r from-emerald-500 to-emerald-600'}`} data-testid="cash-banner">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold opacity-90">Operação de Caixa</p>
            <p className="text-lg font-bold">
              {current
                ? current.status === 'pending_close'
                  ? 'Seu turno está encerrado — finalize a contagem para fechar o caixa'
                  : 'Você está com seu caixa aberto'
                : 'Você não tem caixa aberto'}
              {isAdmin && (
                <span className="opacity-90 text-sm font-normal ml-2">· {openSessions.length} caixa(s) ativo(s) na empresa agora</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {current ? (
              current.status === 'pending_close' ? (
                <span className="bg-white text-orange-700 px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider"><Lock size={12} className="inline mr-1" /> Aguardando contagem</span>
              ) : (
                <span className="bg-white text-emerald-700 px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider"><Unlock size={12} className="inline mr-1" /> Aberto</span>
              )
            ) : (
              <span className="bg-white text-slate-700 px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider"><Lock size={12} className="inline mr-1" /> Fechado</span>
            )}
          </div>
        </div>

        {success && <Alert className="border-green-600 bg-green-50 mb-4"><AlertDescription className="text-green-700">{success}</AlertDescription></Alert>}
        {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

        {/* Caixas pendentes de contagem — turnos encerrados aguardando conferência.
            Filtra fora a sessão que já é a "current" (para não duplicar — current já tem seu próprio form). */}
        {pendingSessions.filter(s => s.id !== current?.id).length > 0 && (
          <div className="bg-orange-50 border-2 border-orange-300 rounded-md p-4 mb-6" data-testid="pending-sessions">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="text-orange-600" size={18} />
              <h3 className="text-base font-bold text-orange-900">
                Caixas aguardando contagem ({pendingSessions.filter(s => s.id !== current?.id).length})
              </h3>
            </div>
            <p className="text-xs text-orange-800 mb-3">
              Turnos encerrados pelo PDV. Selecione um caixa e preencha os valores por forma de pagamento para finalizar o fechamento.
            </p>
            <div className="space-y-2">
              {pendingSessions.filter(s => s.id !== current?.id).map((s) => {
                const isMine = s.user_id === currentUser?.id;
                const isExpanded = adminClose?.session?.id === s.id;
                return (
                  <div key={s.id} className="bg-white border border-orange-200 rounded p-3" data-testid={`pending-session-${s.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="font-bold text-slate-900">
                          {s.user_name} {isMine && <span className="text-xs text-blue-600 ml-1">(você)</span>}
                          {s.terminal && <span className="text-xs text-slate-500 ml-2">· {s.terminal}</span>}
                        </p>
                        <p className="text-xs text-slate-600">
                          Aberto: {new Date(s.opened_at).toLocaleString('pt-BR')} ·
                          Encerrado: {s.shift_ended_at ? new Date(s.shift_ended_at).toLocaleString('pt-BR') : '—'}
                          {s.shift_ended_by_name && ` (por ${s.shift_ended_by_name})`}
                        </p>
                        {isAdmin && (
                          <p className="text-xs text-slate-700 mt-1">
                            Vendas: <strong>R$ {Number(s.total_sales || 0).toFixed(2)}</strong> ·
                            Esperado em dinheiro: <strong className="text-emerald-700">R$ {Number(s.expected_amount || 0).toFixed(2)}</strong>
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setConfModalSessionId(s.id)}
                        className="bg-indigo-700 hover:bg-indigo-800"
                        data-testid={`finalize-pending-${s.id}`}
                      >
                        <ShieldCheck size={14} className="mr-1" /> Conferir Turno
                      </Button>
                    </div>
                    {/* form inline removido — agora conferência é feita em modal completo */}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quadro de todos caixas abertos do dia (admin apenas) */}
        {isAdmin && openSessions.length > 0 && (
          <div className="bg-white rounded-md border border-slate-200 p-4 mb-6" data-testid="all-open-sessions">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Caixas abertos no momento ({openSessions.length})</h3>
              {isAdmin && (
                <span className="text-xs text-blue-600 font-medium flex items-center gap-1">
                  <ShieldCheck size={12} /> Você pode fechar qualquer caixa
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-xs uppercase tracking-wider text-slate-600">
                    <th className="text-left p-2">Operador</th>
                    <th className="text-left p-2">Aberto em</th>
                    {isAdmin && <th className="text-right p-2">Vendas</th>}
                    {isAdmin && <th className="text-right p-2">Dinheiro</th>}
                    {isAdmin && <th className="text-right p-2">PIX</th>}
                    {isAdmin && <th className="text-right p-2">Crédito</th>}
                    {isAdmin && <th className="text-right p-2">Débito</th>}
                    {isAdmin && <th className="text-right p-2">Esperado</th>}
                    <th className="text-center p-2">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {openSessions.map((s) => {
                    const b = s.breakdown || {};
                    const isMine = s.user_id === currentUser?.id;
                    return (
                      <tr key={s.id} className="hover:bg-slate-50" data-testid={`open-session-${s.id}`}>
                        <td className="p-2 font-bold">
                          {s.user_name}
                          {isMine && <span className="ml-1 text-[10px] uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Seu</span>}
                        </td>
                        <td className="p-2 text-xs text-slate-600">{new Date(s.opened_at).toLocaleString('pt-BR')}</td>
                        {isAdmin && <td className="p-2 text-right font-bold text-blue-700">R$ {Number(s.total_sales || 0).toFixed(2)}</td>}
                        {isAdmin && <td className="p-2 text-right">R$ {Number(b.dinheiro || 0).toFixed(2)}</td>}
                        {isAdmin && <td className="p-2 text-right">R$ {Number(b.pix || 0).toFixed(2)}</td>}
                        {isAdmin && <td className="p-2 text-right">R$ {Number(b.credito || 0).toFixed(2)}</td>}
                        {isAdmin && <td className="p-2 text-right">R$ {Number(b.debito || 0).toFixed(2)}</td>}
                        {isAdmin && <td className="p-2 text-right font-bold text-emerald-700">R$ {Number(s.expected_amount || 0).toFixed(2)}</td>}
                        <td className="p-2 text-center">
                          {isMine ? (
                            <span className="text-xs text-slate-500">Use form abaixo</span>
                          ) : isAdmin ? (
                            <Button
                              size="sm"
                              onClick={() => setAdminClose({ session: s, counted: String(s.expected_amount || 0), notes: '' })}
                              className="bg-red-600 hover:bg-red-700 text-white h-7 px-2 text-xs"
                              data-testid={`admin-close-${s.id}`}
                            >
                              <Lock size={10} className="mr-1" /> Fechar
                            </Button>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {current ? (
          <div className={`bg-white rounded-md border-2 p-6 mb-6 ${current.status === 'pending_close' ? 'border-orange-400' : 'border-emerald-400'}`}>
            {current.status === 'pending_close' && (
              <div className="bg-orange-50 border border-orange-300 rounded p-3 mb-4 flex items-start gap-3">
                <Lock className="text-orange-600 shrink-0 mt-0.5" size={20} />
                <div className="text-sm">
                  <p className="font-bold text-orange-800">Turno encerrado — aguardando contagem</p>
                  <p className="text-orange-700">
                    O turno foi encerrado em{' '}
                    <strong>{current.shift_ended_at ? new Date(current.shift_ended_at).toLocaleString('pt-BR') : '—'}</strong>
                    {current.shift_ended_by_name ? ` por ${current.shift_ended_by_name}` : ''}.
                    Preencha abaixo os valores contados para finalizar o fechamento.
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className={`inline-block ${current.status === 'pending_close' ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'} text-xs uppercase tracking-wider font-bold px-2 py-1 rounded`}>
                  {current.status === 'pending_close' ? 'Aguardando contagem' : 'Seu caixa'}
                </span>
                <h2 className="text-2xl font-bold text-slate-900 mt-2">
                  {current.user_name}
                  {current.terminal && <span className="ml-2 text-sm text-slate-500"><Computer size={14} className="inline mr-1" />{current.terminal}</span>}
                </h2>
                <p className="text-xs text-slate-500">Aberto em {new Date(current.opened_at).toLocaleString('pt-BR')}</p>
              </div>
            </div>

            {/* KPIs compactos em uma linha */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
              <div className="bg-slate-50 rounded p-2 text-center">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Saldo Inicial</p>
                <p className="text-base font-bold">R$ {current.initial_amount?.toFixed(2)}</p>
              </div>
              {/* Operadores comuns NÃO veem os totais antes do fechamento — confere às cegas */}
              {isAdmin && (
                <>
                  <div className="bg-blue-50 rounded p-2 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-blue-700">Vendas $</p>
                    <p className="text-base font-bold text-blue-700">R$ {current.total_cash_sales?.toFixed(2)}</p>
                  </div>
                  <div className="bg-emerald-50 rounded p-2 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-emerald-700">Entradas</p>
                    <p className="text-base font-bold text-emerald-700">R$ {current.total_in?.toFixed(2)}</p>
                  </div>
                  <div className="bg-amber-50 rounded p-2 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-amber-700">Saídas</p>
                    <p className="text-base font-bold text-amber-700">R$ {current.total_out?.toFixed(2)}</p>
                  </div>
                  <div className="bg-green-100 rounded p-2 text-center border border-green-300">
                    <p className="text-[10px] uppercase tracking-wider text-green-700">Esperado</p>
                    <p className="text-base font-bold text-green-700">R$ {current.expected_amount?.toFixed(2)}</p>
                  </div>
                </>
              )}
              {!isAdmin && (
                <div className="bg-slate-100 rounded p-2 text-center col-span-2 md:col-span-4 border border-slate-300">
                  <p className="text-xs font-bold text-slate-700">🔒 Fechamento às cegas — informe abaixo o valor recebido por forma de pagamento. Os totais serão exibidos após confirmar.</p>
                </div>
              )}
            </div>

            {/* Breakdown só para admin/manager (esperado por forma de pagamento) */}
            {isAdmin && showCloseForm && <BreakdownSection sessionId={current.id} countedAmount={parseFloat(closeForm.counted_amount) || 0} />}

            {/* Botão para iniciar fechamento (somente visível quando o form ainda não foi aberto) */}
            {!showCloseForm && (
              <div className="mt-4 flex justify-end gap-2 flex-wrap">
                {current.status === 'open' && (
                  <>
                    <Button
                      type="button"
                      onClick={() => { setSuprimentoModal({ amount: '', reason: '' }); setError(''); }}
                      variant="outline"
                      className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 h-10"
                      data-testid="open-suprimento-btn"
                    >
                      <ArrowUpFromLine size={18} className="mr-2" /> Suprimento
                    </Button>
                    <Button
                      type="button"
                      onClick={() => { setSangriaModal({ amount: '', reason: '' }); setError(''); }}
                      variant="outline"
                      className="border-amber-500 text-amber-700 hover:bg-amber-50 h-10"
                      data-testid="open-sangria-btn"
                    >
                      <ArrowDownToLine size={18} className="mr-2" /> Sangria
                    </Button>
                  </>
                )}
                <Button
                  type="button"
                  onClick={() => {
                    // Se turno já encerrado (pending_close), abre modal completo de conferência
                    if (current?.status === 'pending_close') {
                      setConfModalSessionId(current.id);
                    } else {
                      setShowCloseForm(true);
                    }
                  }}
                  className="bg-red-600 hover:bg-red-700 h-10"
                  data-testid="open-close-form-btn"
                >
                  <Lock size={18} className="mr-2" />
                  {current?.status === 'pending_close' ? 'Conferir Turno (Modal Completo)' : 'Iniciar Fechamento do Caixa'}
                </Button>
              </div>
            )}

            {/* Form de fechamento por forma de pagamento — só visível ao clicar "Iniciar Fechamento" */}
            {showCloseForm && (
            <form onSubmit={handleClose} className="space-y-4 mt-4" data-testid="close-form">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-slate-900 uppercase tracking-wider">💰 Valores contados por forma de pagamento</p>
                <button type="button" onClick={() => setShowCloseForm(false)} className="text-xs text-slate-500 hover:text-slate-900" data-testid="close-form-cancel">✕ Cancelar</button>
              </div>
              {!isAdmin && (
                <p className="text-xs text-slate-600">
                  🔒 Fechamento às cegas — informe os valores recebidos. Os totais e diferenças serão exibidos apenas para o administrador depois.
                </p>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Dinheiro (R$)</Label>
                  <Input type="number" step="0.01" required value={closeForm.counted_amount}
                    onChange={(e) => setCloseForm({ ...closeForm, counted_amount: e.target.value })}
                    data-testid="close-counted" placeholder="0,00" />
                </div>
                {CLOSING_METHODS.filter((m) => m.key !== 'dinheiro' && m.key !== 'cartao').map((m) => (
                  <div key={m.key} className="space-y-1">
                    <Label className="text-xs">{m.label} (R$)</Label>
                    <Input type="number" step="0.01" value={closeForm.counted[m.key]}
                      onChange={(e) => setCloseForm({ ...closeForm, counted: { ...closeForm.counted, [m.key]: e.target.value } })}
                      data-testid={`close-counted-${m.key}`} placeholder="0,00" />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs">Observações</Label>
                  <Input value={closeForm.notes} onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })} placeholder="Ex.: troca de turno, sangria etc." />
                </div>
                <Button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700 h-10" data-testid="close-cash-btn">
                  <Lock size={18} className="mr-2" /> Fechar Caixa
                </Button>
              </div>
            </form>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-md border border-slate-200 p-6 mb-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-1">Abrir o seu caixa</h2>
            <p className="text-sm text-slate-600 mb-4">
              Você está logado como <strong>{currentUser?.name || currentUser?.email}</strong>.
              O caixa será aberto em <strong>seu nome</strong>.
            </p>
            <form onSubmit={handleOpen} className="space-y-3" data-testid="open-form">
              {isAdmin && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="open-for-other"
                    checked={openForOther}
                    onChange={(e) => {
                      setOpenForOther(e.target.checked);
                      if (!e.target.checked) {
                        setOpenForm({ ...openForm, operator_id: '', operator_password: '' });
                      }
                    }}
                    className="w-5 h-5 mt-0.5"
                    data-testid="open-for-other-toggle"
                  />
                  <label htmlFor="open-for-other" className="cursor-pointer flex-1 text-sm">
                    <strong>Abrir o caixa para outro operador</strong> (admin) — precisa da senha do operador
                  </label>
                </div>
              )}

              {isAdmin && openForOther && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Operador *</Label>
                    <select
                      required
                      value={openForm.operator_id}
                      onChange={(e) => setOpenForm({ ...openForm, operator_id: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                      data-testid="open-operator"
                    >
                      <option value="">Selecione o operador...</option>
                      {operators.filter(op => op.id !== currentUser?.id).map(op => (
                        <option key={op.id} value={op.id}>{op.name} ({op.email})</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label>Senha do Operador *</Label>
                    <Input
                      type="password"
                      required
                      value={openForm.operator_password}
                      onChange={(e) => setOpenForm({ ...openForm, operator_password: e.target.value })}
                      placeholder="Senha do operador escolhido"
                      data-testid="open-operator-password"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div className="space-y-1">
                  <Label>Valor Inicial em Dinheiro (R$) *</Label>
                  <Input
                    type="number" step="0.01" required
                    value={openForm.initial_amount}
                    onChange={(e) => setOpenForm({ ...openForm, initial_amount: e.target.value })}
                    placeholder="Ex: 100"
                    data-testid="open-initial"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Terminal / PDV (opcional)</Label>
                  <Input
                    value={openForm.terminal}
                    onChange={(e) => setOpenForm({ ...openForm, terminal: e.target.value })}
                    placeholder="Ex: PDV-01"
                  />
                </div>
                <Button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700 h-10" data-testid="open-cash-btn">
                  <Unlock size={18} className="mr-2" /> Abrir Caixa
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                💡 Cada operador tem o seu próprio caixa. Você só vê e opera <strong>o seu</strong>.
              </p>
            </form>
          </div>
        )}

        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
            <h3 className="font-bold text-slate-900">Histórico (meus caixas)</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-3 py-2 text-left">Abertura</th>
                <th className="px-3 py-2 text-left">Fechamento</th>
                <th className="px-3 py-2 text-left">Terminal</th>
                <th className="px-3 py-2 text-right">Inicial</th>
                <th className="px-3 py-2 text-right">Vendas $</th>
                <th className="px-3 py-2 text-right">Esperado</th>
                <th className="px-3 py-2 text-right">Contado</th>
                <th className="px-3 py-2 text-right">Diferença</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {history.length === 0 && <tr><td colSpan={9} className="text-center py-6 text-slate-400">Nenhum histórico</td></tr>}
              {history.map(h => (
                <tr key={h.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2">{new Date(h.opened_at).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2">{h.closed_at ? new Date(h.closed_at).toLocaleString('pt-BR') : '-'}</td>
                  <td className="px-3 py-2">{h.terminal || '-'}</td>
                  <td className="px-3 py-2 text-right">R$ {h.initial_amount?.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-blue-600">R$ {h.total_cash_sales?.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">R$ {h.expected_amount?.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">{h.counted_amount != null ? `R$ ${h.counted_amount.toFixed(2)}` : '-'}</td>
                  <td className={`px-3 py-2 text-right font-bold ${(h.difference || 0) < 0 ? 'text-red-600' : (h.difference || 0) > 0 ? 'text-yellow-700' : 'text-green-700'}`}>
                    {h.difference != null ? `R$ ${h.difference.toFixed(2)}` : '-'}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-xs uppercase tracking-wider font-bold px-2 py-1 rounded ${h.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-700'}`}>{h.status === 'open' ? 'Aberto' : 'Fechado'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Admin: Fechar caixa de outro operador (com breakdown por forma de pagamento) */}
      {adminClose && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" {...overlayClose(() => setAdminClose(null))}>
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-close-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="text-red-600" size={24} />
                <h3 className="text-xl font-bold text-slate-900">Fechar Caixa pelo Admin</h3>
              </div>
              <button onClick={() => setAdminClose(null)} className="w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center"><X size={16}/></button>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded p-3 mb-3 text-sm">
              <p><strong>Operador:</strong> {adminClose.session.user_name}</p>
              <p><strong>Aberto em:</strong> {new Date(adminClose.session.opened_at).toLocaleString('pt-BR')}</p>
              <p><strong>Esperado em caixa:</strong> <span className="font-bold text-emerald-700">R$ {Number(adminClose.session.expected_amount || 0).toFixed(2)}</span></p>
              <p><strong>Total vendas:</strong> R$ {Number(adminClose.session.total_sales || 0).toFixed(2)}</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-300 rounded p-2 text-xs text-yellow-900 mb-3">
              <strong>Atenção:</strong> Você está fechando o caixa em nome do operador. Informe os valores recebidos por forma de pagamento. O fechamento ficará registrado com seu nome.
            </div>
            {error && <Alert variant="destructive" className="mb-3"><AlertDescription>{error}</AlertDescription></Alert>}
            <BlindCloseForm
              sessionId={adminClose.session.id}
              onClosed={() => {
                setAdminClose(null);
                setSuccess(`Caixa de ${adminClose.session.user_name} fechado pelo administrador`);
                setTimeout(() => setSuccess(''), 5000);
                load();
              }}
              onError={(msg) => setError(msg)}
            />
          </div>
        </div>
      )}

      {/* Modal: Suprimento (entrada de dinheiro mantendo o caixa aberto) */}
      {suprimentoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" {...overlayClose(() => setSuprimentoModal(null))}>
          <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="suprimento-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ArrowUpFromLine className="text-emerald-600" size={24} />
                <h3 className="text-xl font-bold text-slate-900">Suprimento de Caixa</h3>
              </div>
              <button onClick={() => setSuprimentoModal(null)} className="w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center"><X size={16}/></button>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 mb-3 text-xs text-emerald-900">
              Adiciona dinheiro ao caixa (por exemplo, troco extra vindo do cofre, reforço do gerente). O caixa <strong>continua aberto</strong> e o valor é somado ao saldo esperado.
            </div>
            <form onSubmit={handleSuprimento} className="space-y-3">
              <div>
                <Label>Valor do Suprimento (R$) *</Label>
                <Input
                  type="number" step="0.01" min="0.01" required autoFocus
                  value={suprimentoModal.amount}
                  onChange={(e) => setSuprimentoModal({ ...suprimentoModal, amount: e.target.value })}
                  data-testid="suprimento-amount"
                  placeholder="0,00"
                />
              </div>
              <div>
                <Label>Origem / Motivo *</Label>
                <Input
                  value={suprimentoModal.reason}
                  onChange={(e) => setSuprimentoModal({ ...suprimentoModal, reason: e.target.value })}
                  placeholder="Ex.: troco extra do cofre, reforço de caixa…"
                  data-testid="suprimento-reason"
                />
                <p className="text-[10px] text-slate-500 mt-1">Mínimo 3 caracteres.</p>
              </div>
              {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setSuprimentoModal(null)} className="flex-1">Cancelar</Button>
                <Button type="submit" disabled={loading} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="suprimento-confirm">
                  <ArrowUpFromLine size={16} className="mr-2" /> {loading ? 'Registrando...' : 'Registrar Suprimento'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Sangria (retirada de dinheiro mantendo o caixa aberto) */}
      {sangriaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" {...overlayClose(() => setSangriaModal(null))}>
          <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="sangria-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ArrowDownToLine className="text-amber-600" size={24} />
                <h3 className="text-xl font-bold text-slate-900">Sangria de Caixa</h3>
              </div>
              <button onClick={() => setSangriaModal(null)} className="w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center"><X size={16}/></button>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-3 text-xs text-amber-900">
              Retira dinheiro do caixa (por exemplo, recolhido pelo gerente ou transferido para o cofre). O caixa <strong>continua aberto</strong> e o valor é deduzido do saldo esperado.
            </div>
            <form onSubmit={handleSangria} className="space-y-3">
              <div>
                <Label>Valor da Sangria (R$) *</Label>
                <Input
                  type="number" step="0.01" min="0.01" required autoFocus
                  value={sangriaModal.amount}
                  onChange={(e) => setSangriaModal({ ...sangriaModal, amount: e.target.value })}
                  data-testid="sangria-amount"
                  placeholder="0,00"
                />
              </div>
              <div>
                <Label>Motivo / Destino *</Label>
                <Input
                  value={sangriaModal.reason}
                  onChange={(e) => setSangriaModal({ ...sangriaModal, reason: e.target.value })}
                  placeholder="Ex.: recolhido pelo gerente, depósito banco, troco no cofre…"
                  data-testid="sangria-reason"
                />
                <p className="text-[10px] text-slate-500 mt-1">Mínimo 3 caracteres.</p>
              </div>
              {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setSangriaModal(null)} className="flex-1">Cancelar</Button>
                <Button type="submit" disabled={loading} className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="sangria-confirm">
                  <ArrowDownToLine size={16} className="mr-2" /> {loading ? 'Registrando...' : 'Registrar Sangria'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Transferir caixa para outro operador */}
      {transferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...overlayClose(() => setTransferModal(null))}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="transfer-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <UserCircle size={20} className="text-blue-600" /> Trocar Operador do Caixa
              </h3>
              <button onClick={() => setTransferModal(null)} className="w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center"><X size={16}/></button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3 text-sm">
              <p><strong>Operador atual:</strong> {transferModal.session.user_name}</p>
              <p><strong>Saldo esperado:</strong> R$ {Number(transferModal.session.expected_amount || 0).toFixed(2)}</p>
              <p className="text-xs text-blue-900 mt-1">
                O caixa continuará aberto com todas as vendas. Apenas o operador responsável muda.
              </p>
            </div>

            <form onSubmit={handleTransfer} className="space-y-3">
              <div>
                <Label>Novo Operador *</Label>
                <select
                  required
                  value={transferModal.new_operator_id}
                  onChange={(e) => setTransferModal({ ...transferModal, new_operator_id: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                  data-testid="transfer-new-operator"
                >
                  <option value="">Selecione...</option>
                  {operators.filter(op => op.id !== transferModal.session.user_id).map(op => (
                    <option key={op.id} value={op.id}>{op.name} ({op.email})</option>
                  ))}
                </select>
              </div>

              {!isAdmin && (
                <div>
                  <Label>Senha do Novo Operador *</Label>
                  <Input
                    type="password"
                    required
                    value={transferModal.new_operator_password}
                    onChange={(e) => setTransferModal({ ...transferModal, new_operator_password: e.target.value })}
                    placeholder="Senha"
                    data-testid="transfer-password"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">Como você não é admin, o novo operador precisa autorizar com a senha dele.</p>
                </div>
              )}
              {isAdmin && (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs text-emerald-800 flex items-center gap-2">
                  <ShieldCheck size={14} /> Como admin, você não precisa da senha do novo operador.
                </div>
              )}

              {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setTransferModal(null)} className="flex-1">Cancelar</Button>
                <Button type="submit" disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="transfer-confirm">
                  {loading ? 'Transferindo...' : 'Transferir Caixa'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Conferência de Turno (novo fluxo) */}
      <SessionConferenceModal
        open={!!confModalSessionId}
        sessionId={confModalSessionId}
        onClose={() => setConfModalSessionId(null)}
        onDone={() => { load(); setSuccess('✓ Turno conferido com sucesso'); setTimeout(() => setSuccess(''), 6000); }}
      />

    </>
  );
};
